/**
 * Color Utilities and Constants
 *
 * Centralized color manipulation, contrast calculation, and color conversion utilities.
 * This module provides all color-related functions used throughout the application.
 */

import { TeamColors } from '../domain/entities';

/** Color represented as RGB object */
export interface RGB {
    r: number;
    g: number;
    b: number;
}

/** Color represented as HSL object */
export interface HSL {
    h: number;
    s: number;
    l: number;
}

/* ========== COLOR CONVERSION UTILITIES ========== */

/**
 * Parse color string to RGB object
 * Supports hex format (e.g., '#ff0000' or 'ff0000') and rgb/rgba format (e.g., 'rgb(255, 0, 0)' or 'rgba(255, 0, 0, 0.5)')
 * @param colorString - Color string in hex or rgb/rgba format
 * @returns RGB object with r, g, b values (0-255)
 */
export const parseColor = (colorString: string): RGB => {
    const trimmed = colorString.trim();

    // Handle rgba/rgb format
    if (trimmed.startsWith('rgba(') || trimmed.startsWith('rgb(')) {
        const rgbaMatch = trimmed.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
        if (rgbaMatch) {
            return {
                r: parseInt(rgbaMatch[1], 10),
                g: parseInt(rgbaMatch[2], 10),
                b: parseInt(rgbaMatch[3], 10),
            };
        }
    }

    // Handle hex format
    const cleaned = trimmed.replace('#', '');
    const r = parseInt(cleaned.substring(0, 2), 16);
    const g = parseInt(cleaned.substring(2, 4), 16);
    const b = parseInt(cleaned.substring(4, 6), 16);
    return { r, g, b };
};

/**
 * Convert RGB object to hex string
 * @param rgb - RGB object
 * @returns Hex color string (e.g., '#ff0000')
 */
export const rgbToHex = (rgb: RGB): string => {
    const toHex = (n: number) => {
        const hex = n.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
};

/**
 * Convert RGB object to CSS RGB string for use in CSS variables
 * @param rgb - RGB object
 * @returns CSS RGB string (e.g., '255, 0, 0')
 */
export const rgbToString = (rgb: RGB): string => {
    return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
};

/**
 * Convert RGB to HSL
 * @param rgb - RGB object
 * @returns HSL object with h (0-360), s (0-100), l (0-100)
 */
export const rgbToHsl = (rgb: RGB): HSL => {
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                break;
            case g:
                h = ((b - r) / d + 2) / 6;
                break;
            case b:
                h = ((r - g) / d + 4) / 6;
                break;
        }
    }

    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100),
    };
};

/**
 * Convert HSL to RGB
 * @param hsl - HSL object
 * @returns RGB object
 */
export const hslToRgb = (hsl: HSL): RGB => {
    const h = hsl.h / 360;
    const s = hsl.s / 100;
    const l = hsl.l / 100;

    let r, g, b;

    if (s === 0) {
        r = g = b = l;
    } else {
        const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255),
    };
};

/* ========== COLOR ANALYSIS UTILITIES ========== */

/**
 * Calculate perceived luminance of a color (0-1)
 * Uses relative luminance formula from WCAG
 * @param rgb - RGB object
 * @returns Luminance value (0 = black, 1 = white)
 */
export const getLuminance = (rgb: RGB): number => {
    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(val => {
        val = val / 255;
        return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Check if a color is considered light
 * @param rgb - RGB object
 * @returns true if color is light, false if dark
 */
export const isLightColor = (rgb: RGB): boolean => {
    return getLuminance(rgb) > 0.5;
};

/**
 * Check if a color is considered dark
 * @param rgb - RGB object
 * @returns true if color is dark, false if light
 */
export const isDarkColor = (rgb: RGB): boolean => {
    return !isLightColor(rgb);
};

/**
 * Calculate contrast ratio between two colors
 * Uses WCAG contrast ratio formula (1-21)
 * @param rgb1 - First RGB color
 * @param rgb2 - Second RGB color
 * @returns Contrast ratio (1 = no contrast, 21 = maximum contrast)
 */
export const getContrast = (rgb1: RGB, rgb2: RGB): number => {
    const lum1 = getLuminance(rgb1);
    const lum2 = getLuminance(rgb2);
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    return (lighter + 0.05) / (darker + 0.05);
};

/* ========== COLOR GENERATION UTILITIES ========== */

/**
 * Get a contrasting color for use as text on an accent background
 * Returns either white or black depending on the accent color brightness
 * @param accentColorHex - Accent color in hex format
 * @returns Hex color for contrasting text (#ffffff or #000000)
 */
export const getContrastColor = (accentColorHex: string): string => {
    const rgb = parseColor(accentColorHex);
    return isLightColor(rgb) ? '#000000' : '#ffffff';
};

/**
 * Get a readable version of an accent color for text on a background
 * Adjusts lightness to ensure sufficient contrast (WCAG AA)
 * @param accentColorHex - Team accent color in hex
 * @param backgroundColorHex - Background color in hex
 * @param minContrast - Minimum contrast ratio required (default 4.5 for WCAG AA)
 * @returns Adjusted hex color with sufficient contrast
 */
export const getReadableAccentColor = (
    accentColorHex: string,
    backgroundColorHex: string,
    minContrast: number = 4.5
): string => {
    const accentRgb = parseColor(accentColorHex);
    const bgRgb = parseColor(backgroundColorHex);

    let currentContrast = getContrast(accentRgb, bgRgb);

    if (currentContrast >= minContrast) {
        return accentColorHex;
    }

    const hsl = rgbToHsl(accentRgb);
    const bgIsLight = isLightColor(bgRgb);

    // Adjust lightness incrementally
    for (let step = 1; step <= 50; step++) {
        let adjustedHsl = { ...hsl };

        if (bgIsLight) {
            // On light background, darken the accent color
            adjustedHsl.l = Math.max(0, hsl.l - step * 2);
        } else {
            // On dark background, lighten the accent color
            adjustedHsl.l = Math.min(100, hsl.l + step * 2);
        }

        const adjustedRgb = hslToRgb(adjustedHsl);
        const newContrast = getContrast(adjustedRgb, bgRgb);

        if (newContrast >= minContrast) {
            return rgbToHex(adjustedRgb);
        }
    }

    // Fallback to black or white
    return bgIsLight ? '#000000' : '#ffffff';
};

/**
 * Get an "away" color that contrasts with the "home" color
 * Tries home and away primary colors first, then uses computed contrast
 * @param homeColors - Home team colors
 * @param awayColors - Away team colors
 * @param minContrast - Minimum contrast ratio (default 1.5)
 * @returns An away team color hex string that contrasts with home color
 */
export const getAwayColor = (homeColors: TeamColors, awayColors: TeamColors, minContrast = 1.5): string => {
    const homeRgb = parseColor(homeColors.primary);
    const awayPrimaryRgb = parseColor(awayColors.primary);

    // Check if away primary contrasts enough with home
    if (getContrast(homeRgb, awayPrimaryRgb) >= minContrast) {
        return awayColors.primary;
    }

    // Try away secondary color
    const awaySecondaryRgb = parseColor(awayColors.secondary);
    if (getContrast(homeRgb, awaySecondaryRgb) >= minContrast) {
        return awayColors.secondary;
    }

    // Fallback: compute a contrasting color by adjusting lightness
    const hsl = rgbToHsl(awayPrimaryRgb);
    const newL = hsl.l > 0.5 ? Math.max(0, hsl.l - 0.4) : Math.min(1, hsl.l + 0.4);
    const adjustedHsl: HSL = { h: hsl.h, s: hsl.s, l: newL };
    const adjustedRgb = hslToRgb(adjustedHsl);
    return rgbToHex(adjustedRgb);
};

/* ========== THEME-AWARE COLOR UTILITIES ========== */

/**
 * Get the current theme's background color from CSS variables
 * @returns RGB object of current background color
 */
export const getCurrentBackgroundColor = (): RGB => {
    const bgColorStr = getComputedStyle(document.documentElement)
        .getPropertyValue('--secondary-color')
        .trim();
    return parseColor(bgColorStr);
};

/**
 * Get readable text color for an accent on the current theme background
 * @param accentColorHex - Accent color in hex
 * @returns Readable hex color for text
 */
export const getThemeReadableAccentColor = (accentColorHex: string): string => {
    const bgColor = getCurrentBackgroundColor();
    const bgHex = rgbToHex(bgColor);
    return getReadableAccentColor(accentColorHex, bgHex, 4.5);
};

/* ========== COLOR CONSTANTS ========== */

/**
 * Standard color palette for the application
 */
export const COLOR_PALETTE = {
    // Status colors
    success: '#00ff9b',
    error: '#ff4d4d',
    warning: '#FFC107',
    info: '#269fff',

    // Semantic colors
    positive: '#00ff9b',
    negative: '#ff4d4d',

    // Theme colors (defaults, overridden at runtime)
    accentDefault: '#00ff9b',
    textDefault: '#e0e0e0',
    backgroundDark: '#1e1e1e',
    backgroundLight: '#fafafa',
} as const;

/**
 * Standard shadow styles for components
 */
export const SHADOW_PRESETS = {
    sm: '0 2px 6px rgba(0, 0, 0, 0.2)',
    md: '0 4px 12px rgba(0, 0, 0, 0.3)',
    lg: '0 8px 24px rgba(0, 0, 0, 0.4)',
} as const;

/**
 * Standard glow effects
 */
export const GLOW_PRESETS = {
    subtle: (accentRgb: string = '0, 255, 155') =>
        `0 0 12px rgba(${accentRgb}, 0.15), ${SHADOW_PRESETS.sm}`,
    medium: (accentRgb: string = '0, 255, 155') =>
        `0 0 20px rgba(${accentRgb}, 0.25), ${SHADOW_PRESETS.md}`,
    strong: (accentRgb: string = '0, 255, 155') =>
        `0 0 30px rgba(${accentRgb}, 0.35), ${SHADOW_PRESETS.lg}`,
} as const;
